import { FindexClient } from "@/integrations/findex";
import { useCurrentUser } from "@/modules/user/hooks/useCurrentUser";
import type { FindexAsset } from "@/types/models/asset";
import { tryOnMounted } from "@vueuse/core";
import { getRefId } from "findef";
import { uniqueBy } from "math";

const currentCompany = ref<FindexAsset | null>(null);
const companies = ref<Array<FindexAsset>>([]);
const loading = ref<boolean>(false);

export type UseIR = {
  currentCompany: ComputedRef<FindexAsset | null>;
  companies: ComputedRef<Array<FindexAsset>>;
  setCompany: (company: FindexAsset) => void;
  setCompanyId: (companyId: string) => void;
  isLoading: ComputedRef<boolean>;
  isIRAdmin: ComputedRef<boolean>;
  updateCompanies: () => Promise<Array<FindexAsset>>;
  updateCompany: (updated: FindexAsset) => void;
};

export const getNewCompanies = async (): Promise<Array<FindexAsset>> => {
  const [assets] = await FindexClient.getAdministratedAssets();
  return ((assets || []) as unknown as any as FindexAsset[]).filter(
    (it) => !!it
  );
};

const updateCompanies = async (): Promise<Array<FindexAsset>> => {
  loading.value = true;
  const newCompanies = await getNewCompanies();
  const oldCompanies = companies.value || [];
  const nextCompanies = uniqueBy(
    [...oldCompanies, ...newCompanies].filter((it) => !!it),
    (it) => getRefId(it)
  );
  companies.value = nextCompanies;
  loading.value = false;
  return nextCompanies;
};

export const useIR = (): UseIR => {
  const { user } = useCurrentUser();

  const setCompany = (company: FindexAsset) => {
    currentCompany.value = company;
  };

  const setCompanyId = (companyId: string) => {
    const company = companies.value.find((it) => getRefId(it) === companyId);
    if (!company) {
      console.warn(`company ${companyId} not found`);
      return;
    }
    setCompany(company);
  };

  const updateCompany = (updated: FindexAsset) => {
    if (!updated?._id) return;
    const index = companies.value.findIndex(
      (c) => getRefId(c) === getRefId(updated)
    );
    if (index !== -1) {
      Object.assign(companies.value[index], updated);
    }
    if (
      currentCompany.value &&
      getRefId(currentCompany.value) === getRefId(updated)
    ) {
      Object.assign(currentCompany.value, updated);
    }
  };

  tryOnMounted(() => {
    Promise.resolve().then(() => {
      (async () => {
        if (!currentCompany.value) {
          const comps = await updateCompanies();
          if (comps.length > 0) {
            setCompany(comps[0]);
          }
        }
      })().catch((e) => console.error(e));
    });
  });

  const isIRAdmin = computed(() => {
    const administrated = user.value.administratedAssets || [];
    if (administrated.length > 0) return true;
    if (companies.value.length > 0) return true;
    if (currentCompany.value) return true;
    return false;
  });

  return {
    currentCompany: computed(() => currentCompany.value),
    companies: computed(() => companies.value),
    isLoading: computed(() => loading.value),
    isIRAdmin,
    setCompany,
    setCompanyId,
    updateCompanies,
    updateCompany: updateCompany,
  };
};
